/**
 * IHS-11 Venue + referee — stadium hero card on top, referee card below.
 *
 * Hero card: full-width 90-100px tall, background = venue image with
 * dark overlay, centered venue name + capacity.
 *
 * Referee card: light grey block with the referee's country flag (small
 * circular badge from flagcdn) inline before the referee's name, and a
 * small "Referee" label underneath.
 */

import type { ReactElement } from 'react';
import { useTranslation } from '../../../i18n/index.js';
import { useTenantConfig } from '../../../tenant/useTenantConfig.js';
import type { IhsVenueRefereeData } from '../../../types/ihs.js';
import { formatInteger } from '../../../utils/numbers.js';
import { IhsCard, dashable } from './IhsCard.js';
import { CountryFlag } from '../../primitives/CountryFlag.js';
import { MediaHero } from '../../primitives/MediaHero.js';
import styles from './IhsVenueReferee.module.css';

export function IhsVenueReferee({
  data,
}: {
  data: IhsVenueRefereeData;
}): ReactElement {
  const { t } = useTranslation();
  const tenant = useTenantConfig();
  const locale =
    tenant.kind === 'live' || tenant.kind === 'paused'
      ? tenant.config.market.locale
      : 'en-GB';
  const { venue, referee } = data;

  const title =
    data.header?.title ??
    t('ihs.venueReferee.title', { defaultValue: 'Other info' });

  const heroSubtitle =
    venue.capacity !== null
      ? `${formatInteger(venue.capacity, locale)} ${t(
          'ihs.venueReferee.capacitySeats',
          { count: venue.capacity, defaultValue: 'seats' },
        )}`
      : venue.city ?? undefined;

  return (
    <IhsCard title={title}>
      {/* Stadium hero — extracted to MediaHero primitive (Phase 0b).
       * Preserve the legacy em-dash fallback for a null venue.name via
       * `dashable`, matching the previous render. */}
      <div className={styles.heroWrap}>
        <MediaHero
          {...(venue.imageUrl ? { imageUrl: venue.imageUrl } : {})}
          fallback="gradient"
          title={dashable(venue.name)}
          {...(heroSubtitle !== undefined ? { subtitle: heroSubtitle } : {})}
          heightPx={96}
        />
      </div>

      {/* Referee card */}
      <div className={styles.refereeCard}>
        <div className={styles.refBody}>
          <div className={styles.refNameRow}>
            <CountryFlag code={referee.nationalityCode} size={18} />
            <span className={styles.refName}>{dashable(referee.name)}</span>
          </div>
          <div className={styles.refLabel}>
            {t('ihs.venueReferee.referee', { defaultValue: 'Referee' })}
            {referee.nationalityLabel ? ` · ${referee.nationalityLabel}` : ''}
          </div>
        </div>
      </div>
    </IhsCard>
  );
}
